import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merges class names with Tailwind CSS conflict resolution.
 * 
 * Combines clsx for conditional classes with tailwind-merge
 * to properly handle Tailwind CSS class conflicts.
 * 
 * @example
 * cn('px-2 py-1', 'px-4') // => 'py-1 px-4'
 * cn('text-red-500', condition && 'text-blue-500')
 * cn({ 'font-bold': isActive }, className)
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Creates a variant-based class generator.
 * Useful for components with multiple variants.
 * 
 * @example
 * const buttonVariants = createVariants({
 *   base: 'px-4 py-2 rounded',
 *   variants: {
 *     intent: {
 *       primary: 'bg-sage text-white',
 *       secondary: 'bg-cream text-navy',
 *     },
 *     size: {
 *       sm: 'text-sm',
 *       md: 'text-base',
 *     },
 *   },
 *   defaultVariants: {
 *     intent: 'primary',
 *     size: 'md',
 *   },
 * });
 */
export function createVariants<
  V extends Record<string, Record<string, string>>,
  D extends { [K in keyof V]?: keyof V[K] }
>(config: {
  base?: string;
  variants: V;
  defaultVariants?: D;
}) {
  return function getClasses(
    props: Partial<{ [K in keyof V]: keyof V[K] }> & { className?: string }
  ): string {
    const { className, ...variantProps } = props;
    
    const classes: string[] = [];
    
    if (config.base) {
      classes.push(config.base);
    }
    
    for (const [variantKey, variantValue] of Object.entries(config.variants)) {
      const key = variantKey as keyof V;
      const selectedValue = variantProps[key] ?? config.defaultVariants?.[key];
      
      if (selectedValue && variantValue[selectedValue as string]) {
        classes.push(variantValue[selectedValue as string]);
      }
    }
    
    if (className) {
      classes.push(className);
    }
    
    return cn(...classes);
  };
}

export default cn;
